import { Injectable } from '@angular/core';
import { PreloadingStrategy, Route } from '@angular/router';
import { Observable, of, Subject } from 'rxjs';

interface PreloadTask {
  priority: number;
  load: () => Observable<any>;
  trigger: Subject<any>;
}

@Injectable({ providedIn: 'root' })
export class SelectivePreloadStrategy implements PreloadingStrategy {
  private queue: PreloadTask[] = [];
  private started = false;

  preload(route: Route, load: () => Observable<any>): Observable<any> {
    if (route.data?.['preload'] !== true) {
      return of(null);
    }

    const priority = Number(route.data?.['preloadPriority'] ?? 999);
    const trigger = new Subject<any>();

    this.queue.push({ priority, load, trigger });
    this.queue.sort((a, b) => a.priority - b.priority);

    if (!this.started) {
      this.started = true;
      this.runQueue();
    }

    return trigger.asObservable();
  }

  private runQueue(): void {
    const nextTask = this.queue.shift();
    if (!nextTask) {
      this.started = false;
      return;
    }

    nextTask.load().subscribe({
      next: (value) => nextTask.trigger.next(value),
      error: () => {
        nextTask.trigger.next(null);
        nextTask.trigger.complete();
        this.runQueue();
      },
      complete: () => {
        nextTask.trigger.complete();
        this.runQueue();
      },
    });
  }
}
